import React from "react";
import styled, {css} from "styled-components";
import media from "styled-media-query";
import breakpoints from "Helpers/BreakpointHelper";
import {rgba} from "polished";
import {ButtonText, PrimaryButton} from "Buttons";
import {H2, Paragraph} from "BaseComponents/Text";
import {LazyComponent, PlaceholderBase,} from "../../framework/lazy/LazyComponent";
import {getResponsiveImage} from "Helpers/ImageHelper";
import {useIsIos} from "Hooks/useIsIos";
import useBreakpoint from "Hooks/useBreakpoint";

export const ParallaxBlock = ({
                                Heading,
                                WhiteTextBackground,
                                Preamble,
                                ResponsiveImage,
                                ButtonText: BtnText,
                                ButtonLink,
                                ButtonTarget,
                                OverlayColor,
                              }) => {
  const Image = getResponsiveImage(ResponsiveImage);
  let alt = ResponsiveImage.Alt;

  const addImageAttributes = alt && alt.length > 0 ? {role: "img", "aria-label": alt} : {"aria-hidden": "true"};
  const isIOS = useIsIos();
  const isMobile = useBreakpoint("mobile");
  return (
    <div className="parallax_element">
      {isIOS && <ParalaxSafari bgImage={Image} overlayColor={OverlayColor} {...addImageAttributes}/>}
      <LazyComponent Placeholder={PlaceholderComponent} source={Image} alt={alt}>
        <ParallaxWrapper
          bgImage={!isIOS ? Image : null}
          overlayColor={OverlayColor}
          isMobile={isMobile}
          {...addImageAttributes}
        >
          <Wrapper overlayColor={OverlayColor}>
            <ContentWrapper whiteTextBackground={WhiteTextBackground}>
              {Heading.length > 0 && <H2 margin="0 0 1rem 0">{Heading}</H2>}
              <Paragraph fontSizeSmall="16px" fontSize="18px">
                {Preamble}
              </Paragraph>
              {ButtonLink.length > 0 && BtnText.length > 0 && (
                <PrimaryButton
                  href={ButtonLink}
                  smallmargin="2rem 0 0 0"
                  margin="2rem 0 0 0"
                  padding="0.1rem 1rem"
                  width="fit-content"
                  ariaLabel={BtnText}
                  target={ButtonTarget}
                >
                  <ButtonText heading={Heading}>{BtnText}</ButtonText>
                </PrimaryButton>
              )}
            </ContentWrapper>
          </Wrapper>
        </ParallaxWrapper>
      </LazyComponent>
    </div>
  );
};

export const PlaceholderComponent = styled(PlaceholderBase)`
    background-attachment: fixed;
`;

const ParalaxSafari = styled.div.attrs(({overlayColor, theme}) => ({
  overlay: overlayColor ? `#${overlayColor}` : theme.colors.none,
}))`
    ${({bgImage}) =>
            bgImage &&
            css`
                background-image: ${({bgImage}) => `url(${bgImage.replace('width=768', 'height=900')})`};
                background-position-x: center;
                background-position-y: 50vh;
                background-size: cover;
                background-repeat: no-repeat;
            `}
    width: 100vw;
    height: 100vh;
    position: sticky;
    position: -webkit-sticky;
    margin-top: -100vh;
    -webkit-touch-callout: inherit;
    max-width: 100%;
    top: 0;
    z-index: -1;

    &:before {
        content: "";
        background: ${({overlay}) =>
                `linear-gradient(to bottom right, ${overlay}, transparent)`};
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
`;

const ParallaxWrapper = styled.div.attrs(({overlayColor, theme, isMobile = false}) => ({
  overlay: overlayColor ? `#${overlayColor}` : theme.colors.none,
  isMobileView: isMobile,
}))`
    background: ${({bgImage, overlay}) =>
            `linear-gradient(to bottom right, ${rgba(overlay, 0.1)}, ${rgba(
                    overlay,
                    0.1
            )})${bgImage ? `, url(${bgImage})` : ''}`};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    min-height: 16rem;
    position: relative;
    overflow: hidden;

    ${media.lessThan(breakpoints.mobile)`		
        background-image: ${({bgImage, overlay, isMobileView}) =>
            `linear-gradient(to bottom right, ${overlay}, ${rgba(
                    overlay,
                    0.5
            )})${bgImage ? `, url(${!isMobileView ? bgImage : bgImage.replace('width=768', 'height=900')})` : ''}`};
    `}
`;

const Wrapper = styled.div.attrs(({overlayColor, theme}) => ({
  overlay: overlayColor ? `#${overlayColor}` : theme.colors.none,
}))`
    width: 100%;
    max-width: ${({theme}) => theme.grid.outer};
    margin: 0 auto;
    padding: 9rem 3rem;

    ${media.greaterThan(breakpoints.desktop)`
        padding: 9rem 1rem;
    `}

    ${media.lessThan(breakpoints.mobile)`
        padding: 6rem 1rem;
    `} @support not (
    -webkit-touch-callout: none) {
    &:before {
        content: "";
        background: ${({overlay}) =>
                `linear-gradient(to bottom right, ${overlay}, transparent)`};
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
}
`;
const ContentWrapper = styled.div`
    max-width: 522px;
    position: relative;

    background: ${({whiteTextBackground}) =>
            whiteTextBackground ? "rgba(247, 247, 247, 0.9)" : ""};
    padding: ${({whiteTextBackground}) => (whiteTextBackground ? "2rem" : "")};
    border-radius: ${({theme}) => theme.border.radius};
`;
